import type { useTranslations } from "next-intl";
import type { MarketplacePreviewListing } from "./marketplace-sample-data";
import type { OwnedListing } from "./marketplace-contracts";

type MarketplaceTranslations = ReturnType<
  typeof useTranslations<"Marketplace">
>;

function formatMoney(
  amountMinor: string,
  currency: string,
  locale: string,
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "code",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(BigInt(amountMinor)) / 100);
}

export function formatRegion(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

export function formatListingPrice(
  listing: Pick<
    MarketplacePreviewListing,
    "pricingType" | "priceMinor" | "priceMaxMinor" | "currency"
  >,
  locale: string,
  contactLabel: string,
): string {
  if (listing.pricingType === "CONTACT" || listing.priceMinor === null) {
    return contactLabel;
  }
  const minimum = formatMoney(listing.priceMinor, listing.currency, locale);
  return listing.pricingType === "RANGE" && listing.priceMaxMinor !== null
    ? `${minimum} – ${formatMoney(listing.priceMaxMinor, listing.currency, locale)}`
    : minimum;
}

export function breedLabel(
  listing: Pick<MarketplacePreviewListing, "breed" | "breedOther">,
  t: MarketplaceTranslations,
): string {
  return listing.breed === "PUREBRED_ARABIAN"
    ? t("breedArabian")
    : listing.breed === "THOROUGHBRED"
      ? t("breedThoroughbred")
      : (listing.breedOther ?? t("breedOther"));
}

export function sexLabel(
  sex: MarketplacePreviewListing["sex"],
  t: MarketplaceTranslations,
): string {
  const keys = {
    COLT: "sexColt",
    FILLY: "sexFilly",
    GELDING: "sexGelding",
    MARE: "sexMare",
    STALLION: "sexStallion",
  } as const;
  return t(keys[sex]);
}

export function statusTranslationKey(status: OwnedListing["status"]) {
  return (
    {
      PENDING: "statusPending",
      APPROVED: "statusApproved",
      REJECTED: "statusRejected",
      SOLD: "statusSold",
      WITHDRAWN: "statusWithdrawn",
      EXPIRED: "statusExpired",
      REMOVED: "statusRemoved",
    } as const
  )[status];
}
